import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import { getStudentVideoUrl, CURRENT_ENVIRONMENT, WEBSOCKET_URL } from "./config";

const LiveMonitorTab_dynamic = ({ connectedStudents = [], students = [] }) => {
  const [activeStudents, setActiveStudents] = useState([]);
  const [previewConnections, setPreviewConnections] = useState({});
  const [previewCanvases, setPreviewCanvases] = useState({});
  const [connectionStates, setConnectionStates] = useState({});
  const [fullscreenStudent, setFullscreenStudent] = useState(null);
  const lastMouse = useRef({ x: null, y: null });
  const mouseHandlerRef = useRef(null);
  const previewCanvasesRef = useRef({});

  // Clean up connections when component unmounts
  useEffect(() => {
    return () => {
      // Close all preview connections
      Object.values(previewConnections).forEach(ws => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      });
    };
  }, [previewConnections]);

  // Fetch active students from backend
  useEffect(() => {
    const fetchActiveStudents = async () => {
      try {
        const response = await fetch("http://localhost:8000/active-students");
        if (response.ok) {
          const data = await response.json();
          console.log("📊 Active students count:", data.length);
          // Map backend data to frontend format
          const mappedStudents = (data || []).map(student => ({
            ...student,
            // If student is in active list and has valid IP, consider them connected
            is_connected: student.ip && student.ip !== 'unknown',
            status: student.status || 'active' // Default to active if status missing
          }));
          setActiveStudents(mappedStudents);
        }
      } catch (error) {
        console.error("❌ Error fetching students:", error);
      }
    };

    fetchActiveStudents();
    const interval = setInterval(fetchActiveStudents, 3000);
    return () => clearInterval(interval);
  }, []);

  // Setup preview connections for all active students
  useEffect(() => {
    activeStudents.forEach(student => {
      if (!previewConnections[student.school_id]) {
        setupPreviewConnection(student);
      }
    });

    // Clean up connections for students no longer active
    Object.keys(previewConnections).forEach(schoolId => {
      const isActive = activeStudents.some(s => s.school_id === schoolId);
      if (!isActive) {
        const ws = previewConnections[schoolId];
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
        setPreviewConnections(prev => {
          const newConns = { ...prev };
          delete newConns[schoolId];
          return newConns;
        });
        setConnectionStates(prev => {
          const newStates = { ...prev };
          delete newStates[schoolId];
          return newStates;
        });
      }
    });
  }, [activeStudents]);

  // Setup preview connection for a student
  const setupPreviewConnection = async (student) => {
    if (!student.ip || student.ip === 'unknown') {
      console.log(`❌ Cannot connect to ${student.school_id}: Invalid IP`);
      return;
    }

    // Determine WebSocket URL based on environment
    let wsUrl;
    if (CURRENT_ENVIRONMENT === 'cloudflare') {
      // Use Cloudflare tunnel WebSocket with student identifier
      wsUrl = `${WEBSOCKET_URL.replace('https:', 'wss:').replace('http:', 'ws:')}/ws/student/${student.school_id}`;
    } else {
      // Use direct IP connection (traditional method)
      wsUrl = `ws://${student.ip}:8081`;
    }
    
    console.log(`🔗 Connecting to ${student.school_id} at ${wsUrl} (env: ${CURRENT_ENVIRONMENT})`);

    // Add immediate connection state feedback
    setConnectionStates(prev => ({
      ...prev,
      [student.school_id]: 'connecting'
    }));

    try {
      const ws = new WebSocket(wsUrl);

      // Reduce timeout to 2 seconds to fail faster
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          console.error(`❌ Timeout for ${student.school_id}`);
          ws.close();
          setConnectionStates(prev => ({
            ...prev,
            [student.school_id]: false
          }));
        }
      }, 2000);

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log(`✅ Connected to ${student.school_id}`);
        setConnectionStates(prev => ({
          ...prev,
          [student.school_id]: true
        }));

        // Request preview stream
        const command = { action: "start_preview_stream" };
        ws.send(JSON.stringify(command));
        
        setPreviewConnections(prev => ({
          ...prev,
          [student.school_id]: ws
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === "screen_update" && data.image) {
            const canvas = previewCanvasesRef.current[student.school_id];
            if (canvas) {
              const img = new Image();
              img.onload = () => {
                const ctx = canvas.getContext('2d');
                canvas.width = 320;
                canvas.height = 240;
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              };
              img.src = `data:image/jpeg;base64,${data.image}`;
            }
          }
        } catch (e) {
          console.error(`❌ Message error for ${student.school_id}:`, e);
        }
      };

      ws.onerror = (error) => {
        console.error(`❌ WebSocket error for ${student.school_id}`);
        setConnectionStates(prev => ({
          ...prev,
          [student.school_id]: false
        }));
      };

      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        if (event.code !== 1000) {
          console.log(`📡 Connection closed for ${student.school_id} (code: ${event.code})`);
        }
        setConnectionStates(prev => ({
          ...prev,
          [student.school_id]: false
        }));
        setPreviewConnections(prev => {
          const updated = { ...prev };
          delete updated[student.school_id];
          return updated;
        });
      };

    } catch (error) {
      console.error(`Failed to connect to ${student.student_name}:`, error);
      setConnectionStates(prev => ({
        ...prev,
        [student.school_id]: false
      }));
    }
  };

  // Handle fullscreen view
  const handleFullscreen = (student) => {
    if (!student.ip || !student.is_connected) {
      alert("Student is not connected");
      return;
    }

    // Create fullscreen container
    const fullscreenDiv = document.createElement('div');
    fullscreenDiv.id = 'fullscreen-control';
    fullscreenDiv.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: black;
      z-index: 10000;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    `;
    
    // Add canvas for HD display
    const canvas = document.createElement('canvas');
    canvas.style.cssText = `
      max-width: 90%;
      max-height: 80%;
      cursor: crosshair;
      background: #333;
    `;
    
    // Control buttons
    const controlPanel = document.createElement('div');
    controlPanel.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      display: flex;
      gap: 10px;
    `;
    
    const takeControlBtn = document.createElement('button');
    takeControlBtn.textContent = '🎮 Take Control';
    takeControlBtn.style.cssText = `
      padding: 12px 24px;
      background: #4CAF50;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 16px;
    `;
    
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '❌ Close';
    closeBtn.style.cssText = `
      padding: 12px 24px;
      background: #f44336;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 16px;
    `;
    
    controlPanel.appendChild(takeControlBtn);
    controlPanel.appendChild(closeBtn);
    fullscreenDiv.appendChild(canvas);
    fullscreenDiv.appendChild(controlPanel);
    document.body.appendChild(fullscreenDiv);
    
    // Setup HD WebSocket connection
    let hdUrl;
    if (CURRENT_ENVIRONMENT === 'cloudflare') {
      // Use Cloudflare tunnel for HD stream
      hdUrl = `${WEBSOCKET_URL.replace('https:', 'wss:').replace('http:', 'ws:')}/ws/student/${student.school_id}`;
    } else {
      // Use direct IP connection (traditional method)
      hdUrl = `ws://${student.ip}:8081`;
    }
    
    const studentName = student.student_name || student.school_id || 'Unknown';
    console.log(`🎬 Setting up HD connection for ${studentName} at ${hdUrl} (env: ${CURRENT_ENVIRONMENT})`);
    const hdWs = new WebSocket(hdUrl);
    let isControlling = false;
    
    hdWs.onopen = () => {
      console.log(`📺 HD connection opened for ${studentName}`);
      const command = { action: "start_hd_stream" };
      hdWs.send(JSON.stringify(command));
      console.log(`📡 HD command sent:`, command);
    };
    
    hdWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log(`📺 HD message from ${student.student_name}:`, data.type);
        if (data.type === "screen_update" && data.image) {
          console.log(`🖼️ HD image for ${student.student_name}, size:`, data.image.length);
          const img = new Image();
          img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            console.log(`✅ HD canvas updated: ${img.width}x${img.height}`);
          };
          img.onerror = () => {
            console.error(`❌ HD image failed to load for ${student.student_name}`);
          };
          img.src = `data:image/jpeg;base64,${data.image}`;
        }
      } catch (e) {
        console.error(`❌ HD stream error for ${student.student_name}:`, e);
      }
    };
    
    hdWs.onerror = (error) => {
      console.error(`❌ HD WebSocket error for ${student.student_name}:`, error);
    };
    
    hdWs.onclose = (event) => {
      console.log(`� HD connection closed for ${studentName}`, {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      });
      
      // Reset control state if connection closes
      if (isControlling) {
        isControlling = false;
        takeControlBtn.textContent = '🎮 Take Control';
        takeControlBtn.style.background = '#4CAF50';
        
        // Remove event listeners
        canvas.removeEventListener('click', handleCanvasClick);
        canvas.removeEventListener('mousemove', handleCanvasMove);
        document.removeEventListener('keydown', handleKeyDown);
      }
    };
    
    // Take control functionality
    takeControlBtn.onclick = () => {
      console.log(`🎮 Take control clicked for ${studentName}, currently controlling:`, isControlling);
      
      if (hdWs.readyState !== WebSocket.OPEN) {
        console.error(`❌ WebSocket not open for ${studentName}. State: ${hdWs.readyState}`);
        return;
      }
      
      if (!isControlling) {
        const enableCmd = { action: "enable_remote_control" };
        hdWs.send(JSON.stringify(enableCmd));
        console.log(`📡 Enable control command sent:`, enableCmd);
        takeControlBtn.textContent = '🔓 Release Control';
        takeControlBtn.style.background = '#FF9800';
        isControlling = true;
        
        // Add mouse and keyboard event listeners
        canvas.addEventListener('click', handleCanvasClick);
        canvas.addEventListener('mousemove', handleCanvasMove);
        canvas.focus();
        canvas.setAttribute('tabindex', '0');
        document.addEventListener('keydown', handleKeyDown);
        console.log(`🖱️ Control event listeners added for ${studentName}`);
      } else {
        const disableCmd = { action: "disable_remote_control" };
        hdWs.send(JSON.stringify(disableCmd));
        console.log(`📡 Disable control command sent:`, disableCmd);
        takeControlBtn.textContent = '🎮 Take Control';
        takeControlBtn.style.background = '#4CAF50';
        isControlling = false;
        
        // Remove event listeners
        canvas.removeEventListener('click', handleCanvasClick);
        canvas.removeEventListener('mousemove', handleCanvasMove);
        document.removeEventListener('keydown', handleKeyDown);
        console.log(`🖱️ Control event listeners removed for ${studentName}`);
      }
    };
    
    function handleCanvasClick(e) {
      if (!isControlling) return;
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) * (canvas.width / rect.width);
      const y = (e.clientY - rect.top) * (canvas.height / rect.height);
      
      const clickCmd = {
        command: "mouse_click",
        x: Math.round(x),
        y: Math.round(y),
        button: e.button === 2 ? 'right' : 'left'
      };
      hdWs.send(JSON.stringify(clickCmd));
      console.log(`🖱️ Click sent to ${studentName}:`, clickCmd);
    }
    
    function handleCanvasMove(e) {
      if (!isControlling) return;
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) * (canvas.width / rect.width);
      const y = (e.clientY - rect.top) * (canvas.height / rect.height);
      
      const moveCmd = {
        command: "mouse_move",
        x: Math.round(x),
        y: Math.round(y)
      };
      hdWs.send(JSON.stringify(moveCmd));
      // Don't log mouse moves as they're too frequent
    }
    
    function handleKeyDown(e) {
      if (!isControlling || e.target !== canvas) return;
      e.preventDefault();
      const keyCmd = {
        command: "key_press",
        key: e.key
      };
      hdWs.send(JSON.stringify(keyCmd));
      console.log(`⌨️ Key sent to ${studentName}:`, keyCmd);
    }
    
    // Close functionality
    closeBtn.onclick = () => {
      hdWs.close();
      document.body.removeChild(fullscreenDiv);
      setFullscreenStudent(null);
    };
    
    // ESC key to close
    const escapeHandler = (e) => {
      if (e.key === 'Escape') {
        closeBtn.click();
        document.removeEventListener('keydown', escapeHandler);
      }
    };
    document.addEventListener('keydown', escapeHandler);
  };

  // Format last seen time
  const formatLastSeen = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    
    if (diffSecs < 60) return `${diffSecs}s ago`;
    if (diffMins < 60) return `${diffMins}m ago`;
    return date.toLocaleTimeString();
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          📺 Live Monitor
        </h2>
        <div className="flex items-center space-x-4 text-sm text-gray-600">
          <span>Total Students: {activeStudents.length}</span>
          <span className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            Connected: {activeStudents.filter(s => s.is_connected).length}
          </span>
          <span className="flex items-center">
            <div className="w-3 h-3 bg-gray-500 rounded-full mr-2"></div>
            Offline: {activeStudents.filter(s => !s.is_connected).length}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {activeStudents.map((student) => (
          <div
            key={student.school_id}
            className="bg-white rounded-lg shadow-lg overflow-hidden border hover:shadow-xl transition-all duration-300"
          >
            <div className="relative group">
              {/* Canvas for screen preview */}
              <canvas 
                ref={(canvas) => {
                  if (canvas && !previewCanvasesRef.current[student.school_id]) {
                    previewCanvasesRef.current[student.school_id] = canvas;
                    
                    // Set canvas dimensions
                    canvas.width = 320;
                    canvas.height = 240;
                    
                    // Setup connection if student is connected and has IP
                    if (student.is_connected && student.ip && !previewConnections[student.school_id]) {
                      setTimeout(() => {
                        setupPreviewConnection(student);
                      }, 100);
                    }
                  }
                }}
                className="w-full h-48 cursor-pointer bg-gray-900 border-0"
                style={{
                  display: 'block',
                  width: '100%',
                  height: '12rem'
                }}
                onClick={() => student.is_connected && handleFullscreen(student)}
              />
              
              {/* Connection status overlay */}
              {!student.is_connected && (
                <div className="absolute inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center">
                  <div className="text-white text-center">
                    <div className="text-red-400 text-2xl mb-2">⚫</div>
                    <div>Offline</div>
                  </div>
                </div>
              )}
              
              {student.is_connected && !connectionStates[student.school_id] && (
                <div className="absolute inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center">
                  <div className="text-white text-center">
                    <div className="text-yellow-400 text-2xl mb-2">🔗</div>
                    <div>Connecting...</div>
                  </div>
                </div>
              )}
              
              {/* Hover overlay for connected students */}
              {student.is_connected && (
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <button
                      onClick={() => handleFullscreen(student)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      📺 View Fullscreen
                    </button>
                  </div>
                </div>
              )}
              
              {/* Connection indicator */}
              <div className="absolute top-2 right-2">
                <div 
                  className={`w-3 h-3 rounded-full ${
                    student.is_connected && connectionStates[student.school_id] 
                      ? 'bg-green-500' 
                      : student.is_connected 
                        ? 'bg-yellow-500' 
                        : 'bg-red-500'
                  }`}
                  title={
                    student.is_connected && connectionStates[student.school_id]
                      ? 'Connected & Streaming'
                      : student.is_connected
                        ? 'Connected but not streaming'
                        : 'Offline'
                  }
                />
              </div>
            </div>
            
            {/* Student info */}
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-lg text-gray-800 truncate">
                  {student.student_name}
                </h3>
                <div className="text-right">
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded block mb-1">
                    {student.school_id}
                  </span>
                  <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                    Form {student.form}
                  </span>
                </div>
              </div>
              
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>IP:</span>
                  <span className="font-mono">
                    {student.ip || 'Not available'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className={`font-medium ${
                    student.is_connected ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {student.is_connected ? 'Online' : 'Offline'}
                  </span>
                </div>
                {student.last_seen && (
                  <div className="flex justify-between">
                    <span>Last Seen:</span>
                    <span>{formatLastSeen(student.last_seen)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {activeStudents.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg">
            No active students found
          </div>
          <div className="text-gray-500 text-sm mt-2">
            Students will appear here when they're registered for this form
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveMonitorTab_dynamic;
